<template>
  <main class="tma-init-page">
    <MascotLoader
      v-if="loading"
      :active="true"
      :label="t('tma.authenticating')"
    />
    <section v-else class="tma-init-content">
      <div v-if="!isInTelegram" class="tma-error">
        <h1>{{ t('tma.openInTelegram') }}</h1>
        <p>{{ t('tma.openInTelegramDescription') }}</p>
      </div>
      <div v-else-if="tmaState?.status === 'error'" class="tma-error">
        <h1>{{ t('tma.authError') }}</h1>
        <p>{{ t('miniApp.email.errors.generic') }}</p>
        <BaseButton monochrome
block
class="tma-retry-btn"
@click="retry">{{
          t('tma.retry')
        }}</BaseButton>
      </div>
      <div v-else class="tma-continuation">
        <h1>{{ t('tmaEmail.continueTitle') }}</h1>
        <p>{{ t('tmaEmail.continueHint') }}</p>
        <strong v-if="emailFlow?.email">{{ emailFlow.email }}</strong>
        <BaseButton
          v-if="tmaState?.status === 'email_required'"
          monochrome
          block
          size="lg"
          data-action="resume"
          @click="tmaAuthFlow?.resume()"
          >{{ t('common.continue') }}</BaseButton
        >
        <BaseButton
          v-if="
            emailFlow?.can_skip === true &&
            emailFlow.revision &&
            tmaState?.status === 'email_required'
          "
          monochrome
          variant="ghost"
          block
          size="lg"
          data-action="skip"
          :loading="tmaAuthFlow?.busy.value"
          @click="tmaAuthFlow?.action('skip')"
          >{{ t('tmaEmail.skip') }}</BaseButton
        >
        <p v-if="tmaAuthFlow?.error.value" role="alert">
          {{ tmaAuthFlow.error.value }}
        </p>
      </div>
      <BaseButton
        v-if="isInTelegram"
        monochrome
        variant="ghost"
        block
        data-action="exit"
        @click="exit"
      >
        {{ t('tmaEmail.exit') }}
      </BaseButton>
    </section>
    <SponsorModal
      v-if="isInTelegram && tmaState?.status === 'sponsor_required'"
      :visible="true"
      :init-data="tmaState.initData"
      :telegram-user="null"
      @result="tmaAuthFlow?.accept($event)"
    />
    <EmailRegistrationModal
      v-if="
        isInTelegram &&
        tmaState?.status === 'email_required' &&
        emailFlow &&
        tmaAuthFlow?.modalOpen.value
      "
      :flow="emailFlow"
      :busy="tmaAuthFlow.busy.value"
      :error="tmaAuthFlow.error.value"
      @submit="tmaAuthFlow.input($event)"
      @action="tmaAuthFlow.action($event)"
      @close="tmaAuthFlow.close()"
    />
    <TotpModal
      v-if="isInTelegram && tmaState?.status === 'totp_required'"
      :visible="true"
      :spa-auth-token="tmaState.authResult?.spa_auth_token ?? ''"
      @success="
        tmaAuthFlow?.accept({ result: 'success', spa_auth_token: $event })
      "
    />
  </main>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseButton from '@/shared/components/base/BaseButton.vue'
import MascotLoader from '@/components/common/MascotLoader.vue'
import type { TmaState } from '@/mini-app'
import type { TmaAuthFlow } from './tmaAuthFlow'
import { readTelegramInitData } from './telegramLaunchParams'
import EmailRegistrationModal from './EmailRegistrationModal.vue'
import SponsorModal from './SponsorModal.vue'
import TotpModal from './TotpModal.vue'

const { t } = useI18n()
const tmaState = inject<Ref<TmaState>>('tmaState')
const tmaAuthFlow = inject<TmaAuthFlow>('tmaAuthFlow')
const isInTelegram = inject<boolean>('isInTelegram', false)
const loading = computed(
  () =>
    isInTelegram &&
    ['idle', 'authenticating'].includes(tmaState?.value.status ?? 'idle'),
)
const emailFlow = computed(() => tmaState?.value.authResult?.email_flow)
onMounted(() => {
  if (isInTelegram && tmaState?.value.initData && tmaState.value.status !== 'error')
    document.cookie = 'tma_user=1; path=/; max-age=315360000; SameSite=Lax'
})
function retry() {
  void tmaAuthFlow?.authenticate(readTelegramInitData(window.Telegram?.WebApp?.initData) ?? tmaState?.value.initData ?? '')
}
function exit() {
  window.Telegram?.WebApp?.close()
}
</script>

<style scoped lang="scss">
.tma-init-page {
  min-height: 100dvh;
  display: grid;
  place-items: center;
  background: var(--ds-surface-canvas);
  color: var(--ds-text);
  padding: 24px 16px;
}
.tma-init-content {
  width: min(400px, 100%);
  text-align: center;
}
.tma-continuation,
.tma-error {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}
.tma-continuation strong {
  overflow-wrap: anywhere;
}
</style>
